import React from 'react';
import { observer } from 'mobx-react';
import { computed } from 'mobx';
import ProgressBar from 'components/progress_bar';
import styled from '@emotion/styled';
import { flexCenter } from '@seedlang/style_mixins';
import { Theme } from '@seedlang/constants';
import Text from 'components/text';
import { BadgeWrapper } from '@seedlang/hoc';
import { depixify, pixify } from '@seedlang/utils';

const Wrapper = styled.div`
  ${flexCenter()}
  width: ${props => props.width};
  height: ${props => props.height};
  margin: ${props => props.margin};
  flex-direction: ${props => props.alignment === 'vertical' ? 'column' : 'row'};
  position: relative;
  cursor: ${props => props.onClick ? 'pointer' : props.cursor || 'default'};
  transition: ${props => props.onClick ? 'transform 0.08s ease' : 'none'};
  ${props => props.onClick && `
    &:active {
      transform: scale(0.97);
    }
  `}
`;

const BadgeIconWrapper = styled.div`
  width: ${props => props.width};
  height: ${props => props.width};
  ${flexCenter()}
  position: relative;
  border: 3px solid ${props => props.fill};
  border-radius: 50%;
  svg {
    width: ${props => props.svgWidth};
    #background {
      fill: ${props => props.fill};
    }
  }
`;

const LevelIndicator = styled.div`
  z-index: ${Theme.z['foreground']};
  background: ${props => props.background};
  ${flexCenter()}
  color: white;
  height: ${props => props.width};
  width: ${props => props.width};
  position: absolute;
  bottom: ${props => props.bottom};
  right: ${props => props.right};
  font-size: ${props => props.fontSize};
  line-height: ${props => props.fontSize};
  text-align: center;
  font-weight: bold;
  border-radius: 50%;
  border: ${props => props.borderWidth} solid ${props => props.borderColor};
`;

const MainIcon = styled.div`
  z-index: ${Theme.z['background']};
`;

const Info = styled.div`
  flex: 1;
  margin: 0 0 0 10px;
`;

@observer
class Badge extends React.Component {

  static defaultProps = {
    alignment: 'horizontal',
    margin: '0 0 10px 0',
    fontSize: 16,
    textMarginBottom: 3,
    bottom: '1px',
    right: '1px',
    levelIndicatorWidth: '20px',
    levelIndicatorBorderWidth: '2px',
    levelIndicatorFontSize: '10px',
    levelIndicatorBorderColor: '#FFF',
  };

  @computed get badgeColor() {
    return this.props.badgeColorOverride || this.props.badgeColor;
  }

  @computed get currentLevel() {
    return this.props.currentLevelOverride || this.props.currentLevel;
  }

  @computed get levelRoman() {
    return this.props.levelRomanOverride || this.props.levelRoman;
  }

  render() {
    return (
      <div
        style={{width: this.props.badgeWidth}}
      >
        <Wrapper
          alignment={this.props.alignment}
          margin={this.props.margin}
          width={this.props.wrapperWidth}
          height={this.props.wrapperHeight}
          cursor={this.props.cursor}
          onClick={this.props.onClick}
        >
          <BadgeIconWrapper
            fill={this.badgeColor}
            width={this.props.width}
            svgWidth={pixify(depixify(this.props.width) - 10)}
            height={this.props.height}
          >
            <MainIcon>
              {this.props.icon}
            </MainIcon>
            {
              this.currentLevel > 0 &&
                <LevelIndicator
                  background={this.badgeColor}
                  bottom={this.props.bottom}
                  right={this.props.right}
                  width={pixify(this.props.levelIndicatorWidth)}
                  fontSize={pixify(this.props.levelIndicatorFontSize)}
                  borderWidth={pixify(this.props.levelIndicatorBorderWidth)}
                  borderColor={this.props.levelIndicatorBorderColor}
                >
                  {this.levelRoman}
                </LevelIndicator>
            }
          </BadgeIconWrapper>
          {
            !this.props.hideInfo &&
              <Info>
                <Text
                  bold
                  fontSize={pixify(this.props.fontSize)}
                  lineHeight='20px'
                  marginTop={this.props.textMarginTop}
                  marginBottom={this.props.textMarginBottom}
                >
                  {this.props.name}
                </Text>
                {
                  this.props.showProgress &&
                    <ProgressBar
                      current={this.props.progressCurrent}
                      total={this.props.progressTotal}
                      width='100%'
                      borderRadius='5px'
                      height='12px'
                    />
                }
              </Info>
          }
        </Wrapper>
      </div>
    );
  }
}

export default BadgeWrapper(Badge);
